import React, { useEffect, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/authContext';
import { useAuth } from './context/authContext';
import { LanguageProvider } from './context/languageContext';
import { ThemeProvider } from './context/themeContext';
import { useUser } from './hooks/useUser';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import PageSeoUpdater from './components/PageSeoUpdater';
import { SnackbarProvider } from './context/snackbarContext';
import { Toaster } from '@/components/ui/toaster';
import ErrorBoundary from './components/ErrorBoundary';
// Conditionally import DevUserSwitcher only in development
let DevUserSwitcher: React.ComponentType<any> | null = null;
if (import.meta.env.DEV) {
  DevUserSwitcher = React.lazy(() => import('./components/DevUserSwitcher'));
}
const PWAInstallBanner = React.lazy(() => import('./components/PWAInstallBanner'));
import { queryClient } from './lib/queryClient';
import './i18n';
import './index.css';
import './App.css';
import { CookieManager } from 'react-cookie-manager';
// Note: react-cookie-manager/style.css doesn't exist in the package
// The package doesn't provide a CSS file, so we skip the import
// If styles are needed, they should be added manually or the package should be updated
import { useServiceWorkerMessages } from './hooks/useServiceWorkerMessages';
import { useTranslation } from 'react-i18next';

// Auto-accept cookies in development mode before React loads
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  const consentData = {
    Analytics: { consented: true, timestamp: new Date().toISOString() },
    Social: { consented: false, timestamp: new Date().toISOString() },
    Advertising: { consented: false, timestamp: new Date().toISOString() }
  };
  document.cookie = `cookie-consent=${JSON.stringify(consentData)}; path=/; max-age=31536000; SameSite=Lax`; // 1 year
}

// Eager-load the lightweight landing page, lazy-load heavier authenticated pages for better initial load performance
import Index from './pages/Index';

// Preload critical authenticated components for faster cold start
const preloadCriticalComponents = () => {
  // Preload HomePage in background for faster navigation
  import('./pages/HomePage');
  // Preload LoginPage for faster auth flow
  import('./pages/LoginPage');
};

// Start preloading immediately
if (typeof window !== 'undefined') {
  // Preload after a short delay to not block initial render
  setTimeout(preloadCriticalComponents, 100);
}

// Create a wrapper for lazy-loaded components that handles offline errors
const createOfflineSafeLazyComponent = (importFn: () => Promise<any>) => {
  return React.lazy(() =>
    importFn().catch(error => {
      // If it's an offline error, show a fallback component
      if (error.message.includes('Failed to fetch dynamically imported module')) {
        return Promise.resolve({
          default: () => {
            // Create a proper React component for the fallback
            const OfflineFallback = () => {
              const { t } = useTranslation();
              return (
                <div className="flex justify-center items-center min-h-screen bg-background">
                  <div className="text-center p-8 max-w-md">
                    <div className="text-6xl mb-4">📱</div>
                    <h1 className="text-2xl font-bold text-foreground mb-2">{t('errors.offlineNavigation.title')}</h1>
                    <p className="text-muted-foreground mb-6">
                      {t('errors.offlineNavigation.description')}
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {t('errors.errorBoundary.refreshButton')}
                    </button>
                  </div>
                </div>
              );
            };
            return <OfflineFallback />;
          }
        });
      }
      throw error; // Re-throw other errors
    })
  );
};

const LoginPage = createOfflineSafeLazyComponent(() => import('./pages/LoginPage'));
const NotFound = createOfflineSafeLazyComponent(() => import('./pages/NotFound'));
const HomePage = createOfflineSafeLazyComponent(() => import('./pages/HomePage'));
const ListDetailPage = createOfflineSafeLazyComponent(() => import('./pages/ListDetailPage'));
const AccountSettingsPage = createOfflineSafeLazyComponent(() => import('./pages/AccountSettingsPage'));
const AccountSecurityPage = createOfflineSafeLazyComponent(() => import('./pages/AccountSecurityPage'));
const AdminImageModelsPage = createOfflineSafeLazyComponent(() => import('./pages/AdminImageModelsPage'));
const ReferralLanding = createOfflineSafeLazyComponent(() => import('./pages/ReferralLanding.tsx'));
const TermsOfService = createOfflineSafeLazyComponent(() => import('./pages/TermsOfService'));
const PrivacyPolicy = createOfflineSafeLazyComponent(() => import('./pages/PrivacyPolicy'));

const LoadingFallback: React.FC = () => (
  <div className="flex justify-center items-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-primary border-solid" />
  </div>
);

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isOptimistic } = useAuth();
  const { user, isLoading } = useUser();

  if (isLoading || isOptimistic) {
    return <LoadingFallback />;
  }

  if (user?.isAdmin !== true) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
};

// Custom hook to detect offline status
const useOfflineStatus = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOffline;
};

// Component that handles service worker messages inside QueryClientProvider
const ServiceWorkerMessageHandler: React.FC = () => {
  const { clearModuleCache } = useServiceWorkerMessages();

  // Add global error handler for React context issues
  useEffect(() => {
    let errorCount = 0;
    const maxErrors = 3; // Only clear cache after multiple context errors

    const handleReactContextError = (event: ErrorEvent) => {
      if (event.error?.message?.includes('Invalid hook call') ||
        event.error?.message?.includes('useContext')) {
        errorCount++;

        // Only clear cache after multiple errors to avoid unnecessary clearing
        if (errorCount >= maxErrors) {
          clearModuleCache();
          errorCount = 0; // Reset counter
        }
      }
    };

    window.addEventListener('error', handleReactContextError);
    return () => window.removeEventListener('error', handleReactContextError);
  }, [clearModuleCache]);

  return null;
};

const App: React.FC = () => {
  const { t } = useTranslation();
  const isOffline = useOfflineStatus();

  // Check if cookie banner is enabled via build flag (default: false)
  const isCookieBannerEnabled = import.meta.env.VITE_ENABLE_COOKIE_BANNER === 'true';

  // Router content - shared between both cookie banner states
  const routerContent = (
    <Router>
      <PageSeoUpdater />
      <ScrollToTop />
      <Header />
      {isOffline && (
        <div className="bg-yellow-100 border-b border-yellow-400 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800/30 dark:text-yellow-200 px-4 py-2 text-center text-sm">
          {t('app.offline', 'You are currently offline. Some features may be limited.')}
        </div>
      )}
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route path="/" element={<Index />} />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <TermsOfService />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PrivacyPolicy />
            </Suspense>
          }
        />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <HomePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lists/:id"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <ListDetailPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lists/:id/print"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <ListDetailPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/r/:code"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ReferralLanding />
            </Suspense>
          }
        />
        <Route
          path="/account-settings"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <AccountSettingsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-security"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <AccountSecurityPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/image-models"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminImageModelsPage />
                </Suspense>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
      <Toaster />
      <Footer />

      {/* Dev User Switcher - Only in development */}
      {DevUserSwitcher && <DevUserSwitcher />}

      {/* PWA Install Banner */}
      <Suspense fallback={null}>
        <PWAInstallBanner />
      </Suspense>

      {/* PWA Test Utils - Only in development */}
      {/* <PWATestUtils /> */}

      {/* Service Worker Migration Prompt - Temporarily disabled for testing */}
      {/*
    <ServiceWorkerMigrationPrompt
      onMigrationComplete={() => {
        console.log('[App] 🎉 Service worker migration completed!');
      }}
      onMigrationSkipped={() => {
        console.log('[App] ⏭️ Service worker migration skipped');
      }}
      autoStart={false}
    />
    */}

      {/* Service Worker Update Prompt */}
      {/* <ServiceWorkerUpdatePrompt /> */}
    </Router>
  );

  // Listen for service worker messages
  useEffect(() => {
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_READY') {
        // Service worker is ready
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  // Add manual cache clearing function to window for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).clearModuleCache = () => {
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_MODULE_CACHE'
          });
        }
      };
      console.log('🔧 Development: Use window.clearModuleCache() to manually clear module cache');
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ServiceWorkerMessageHandler />
      <ThemeProvider>
        <LanguageProvider>
          <ErrorBoundary>
            <SnackbarProvider>
              <AuthProvider>
                {isCookieBannerEnabled ? (
                  <CookieManager
                    cookieKitId="689af594630cbf2991c86e14"
                    displayType="banner"
                    translations={{
                      title: '',
                      message: (
                        <span className="flex items-start gap-3">
                          <img src="/cookie.png" alt="Cookie" className="h-12 w-12 sm:h-14 sm:w-14" />
                          <span className="text-left">
                            <strong className="block">{t('cookie.title')}</strong>
                            <span className="block">{t('cookie.message')}</span>
                          </span>
                        </span>
                      ) as unknown as string,
                    }}
                  >
                    {routerContent}
                  </CookieManager>
                ) : (
                  routerContent
                )}
              </AuthProvider>
            </SnackbarProvider>
          </ErrorBoundary>
        </LanguageProvider>
      </ThemeProvider>

      {/* Dev tools - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
};

export default App;
